/**
 * Keyboard "chord" helpers — a chord is a normalized string representation of a
 * key combination, e.g. "s", "0", "space", "shift+r", "ctrl+shift+c".
 *
 * Normalization rules:
 *  - modifiers in fixed order: ctrl, alt, shift (Cmd/meta is treated as ctrl so
 *    bindings work cross-platform and match the previous Windows-first behavior)
 *  - key is lowercased; Space is "space"
 */
const MODIFIER_KEYS = new Set(['control', 'shift', 'alt', 'meta', 'os']);
/** Build the canonical chord string for a keyboard event. */
export function eventToChord(e) {
    const parts = [];
    if (e.ctrlKey || e.metaKey)
        parts.push('ctrl');
    if (e.altKey)
        parts.push('alt');
    if (e.shiftKey)
        parts.push('shift');
    parts.push(normalizeKey(e.key));
    return parts.join('+');
}
/** True if the event is only a modifier press (no "real" key yet). */
export function isModifierOnly(e) {
    return MODIFIER_KEYS.has(e.key.toLowerCase());
}
function normalizeKey(key) {
    const k = key.toLowerCase();
    if (k === ' ' || k === 'spacebar')
        return 'space';
    return k;
}
const SEGMENT_LABELS = {
    ctrl: 'Ctrl',
    alt: 'Alt',
    shift: 'Shift',
    space: 'Space',
    escape: 'Esc',
    arrowup: '\u2191',
    arrowdown: '\u2193',
    arrowleft: '\u2190',
    arrowright: '\u2192',
    home: 'Home',
    end: 'End',
    enter: 'Enter',
    tab: 'Tab',
    backspace: 'Backspace',
    delete: 'Del',
};
/** Human-readable label for a chord, e.g. "ctrl+shift+c" → "Ctrl + Shift + C". */
export function formatChord(chord) {
    if (!chord)
        return '';
    return chord
        .split('+')
        .map((seg) => SEGMENT_LABELS[seg] ?? (seg.length === 1 ? seg.toUpperCase() : capitalize(seg)))
        .join(' + ');
}
function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
